import * as Sentry from '@sentry/browser';
import { isPlainObject } from 'lodash-es';
import type { CapturedNetworkRequest } from 'posthog-js';

// Effectively same as in `apolloLoggingPlugin`
const SENSITIVE_VARIABLES = [
  'password',
  'auth',
  'authentication',
  'currentPassword',
  'newPassword',
  'token',
  'loginToken',
];

class MaskCapturedNetworkRequestError extends Error {
  constructor(error: unknown) {
    super(
      `Something went wrong when running maskCapturedNetworkRequestFn: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

const parseBodyAsJson = (body: string) => {
  try {
    return JSON.parse(body) as Record<string, unknown>;
  } catch {
    // Request or response bodies may be truncated or replaced with an error
    // message like "[SessionReplay] Timeout while trying to read body". If that
    // happens, we should just abort. It's not caused by our code.
    return null;
  }
};

const redactSensitiveData = (data: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(data).map(([key, value]): [string, unknown] => {
      if (SENSITIVE_VARIABLES.includes(key)) {
        return [key, 'REDACTED'];
      }

      if (isPlainObject(value)) {
        return [key, redactSensitiveData(value as Record<string, unknown>)];
      }

      return [key, value];
    }),
  );

const redactRequestBody = (request: CapturedNetworkRequest) => {
  if (!request.requestBody) return;

  const parsedBody = parseBodyAsJson(request.requestBody);

  if (!parsedBody) {
    request.requestBody = "Couldn't parse request body.";
    return;
  }

  if (parsedBody.variables && isPlainObject(parsedBody.variables)) {
    parsedBody.variables = redactSensitiveData(parsedBody.variables as Record<string, unknown>);
    request.requestBody = JSON.stringify(parsedBody);
  }
};

const redactResponseBody = (request: CapturedNetworkRequest) => {
  if (!request.responseBody) return;

  const parsedBody = parseBodyAsJson(request.responseBody);

  if (!parsedBody) {
    request.responseBody = "Couldn't parse response body.";
    return;
  }

  if (parsedBody.data && isPlainObject(parsedBody.data)) {
    parsedBody.data = redactSensitiveData(parsedBody.data as Record<string, unknown>);
    request.responseBody = JSON.stringify(parsedBody);
  }
};

export const redactRequest = (request: CapturedNetworkRequest) => {
  try {
    redactRequestBody(request);
    redactResponseBody(request);

    return request;
  } catch (error) {
    Sentry.captureException(new MaskCapturedNetworkRequestError(error));
    return null;
  }
};
